<template>
    <div class="w-full"> 
        <div v-if="!loading && transfers && total > 0" v-for="(tr, key) in transfers" :key="tr.id"
            @click='detail_page(tr.id)' 
            class="w-fill flex flex-column surface-0 relative overflow-hidden cursor-pointer
                border-round-2xl
                p-3 xl:p-4
                mb-3 xl:mb-4">
            <div class="w-full flex flex-row
                mb-3 xl:mb-4" >
                <div v-if="tr.sender" class="flex flex flex-column flex-1 overflow-hidden">
                    <span class="flex text-500 font-medium
                        mb-2 xl:mb-3
                        text-xs xl:text-sm">
                        <span v-if="tr.sender.fullname">{{ tr.sender.fullname }}</span>
                        <span v-else>{{ lang('sender') }}</span>
                    </span>
                    <span v-if="tr.sender.amount && tr.sender.amount && tr.sender.currency_code" 
                        class="flex font-medium text-800 white-space-nowrap
                            mb-2 xl:mb-3
                            text-xl xl:text-3xl">
                            {{ get_format_amount(tr.sender.amount, tr.sender.currency_code) }}
                    </span>
                    <div class="flex flex-row align-items-center justify-content-left gap-2">
                        <AvatarGroup v-if="tr.get.code || tr.sender.bank_code">
                            <Avatar v-if="tr.sender.bank_code" :image="getBankImage(tr.sender.bank_code)" class="flex p-avatar-sm" shape="circle" size="small"/>
                            <Avatar v-else :image="getPaySysImage(tr.get.code)" class="flex p-avatar-sm" shape="circle" size="small"/>
                        </AvatarGroup>
                        <div class="text-500 flex white-space-nowrap vertical-align-middle font-medium text-xs xl:text-sm">
                            <span v-if="tr.sender.bank_code">
                                {{ getBankTitle(tr.sender.bank_code) }}
                            </span>
                            <span v-else>
                                {{ getPaySysTitle(tr.get.code) }}
                            </span>
                        </div>
                    </div>
                </div>
                <div v-if="tr.recipient" class="flex flex flex-column flex-1">
                    <span class="flex text-500 font-medium
                        mb-2 xl:mb-3
                        text-xs xl:text-sm">
                        <span v-if="tr.recipient.fields && tr.recipient.fields.fullname">{{ tr.recipient.fields.fullname }}</span>
                        <span v-else>{{ lang('recipient') }}</span>
                    </span>
                    <span v-if="tr.recipient.amount && tr.recipient.amount && tr.recipient.currency_code" 
                        class="flex font-medium text-800 white-space-nowrap
                            mb-2 xl:mb-3
                            text-xl xl:text-3xl">
                            {{ get_format_amount(tr.recipient.amount, tr.recipient.currency_code) }}
                    </span>
                    <div class="flex flex-row align-items-center justify-content-left gap-2">
                        <AvatarGroup v-if="tr.send.code || tr.recipient.bank_code">
                            <Avatar v-if="tr.recipient.bank_code" :image="getBankImage(tr.recipient.bank_code)" class="flex p-avatar-sm" shape="circle" size="small"/>
                            <Avatar v-else :image="getPaySysImage(tr.send.code)" class="flex p-avatar-sm" shape="circle" size="small"/>
                        </AvatarGroup>
                        <div class="text-500 flex white-space-nowrap vertical-align-middle font-medium text-xs xl:text-sm">
                            <span v-if="tr.recipient.bank_code">
                                {{ getBankTitle(tr.recipient.bank_code) }}
                            </span>
                            <span v-else>
                                {{ getPaySysTitle(tr.send.code) }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex flex-row">
                <div v-if="tr.status" class="flex flex-initial pr-2 xl:pr-3">
                    <div :class="'status-'+tr.status" 
                        class="inline-block font-medium border-2 surface-border text-white font-medium align-items-center
                        border-round-md xl:border-round-lg
                        p-1 xl:px-2">
                        <span class="flex text-xs xl:text-sm">
                            {{getStatusLabel(tr.status)}}
                        </span>
                    </div>
                </div>
                <div v-if="tr.dates && tr.dates.created && tr.status" class="flex flex-1">
                    <span class="inline-block text-500 font-medium border-2 border-transparent
                        text-xs xl:text-sm
                        py-1">
                        <span v-if="tr.status=='created' && tr.progress && tr.progress>0">
                            {{ this.lang('timetopay') }} {{ tr.counter }}
                        </span>
                        <span v-else>{{ formatDate(tr.dates.created) }}</span>
                    </span>
                </div>
                <div class="flex">
                    <span v-if="tr.id" class="flex flex-row text-500 font-medium border-round-lg cursor-pointer border-2 py-1 px-2 ml-2 border-transparent">
                        <span class="flex text-xs xl:text-sm">№ {{get_short_id(tr.id)}}</span>
                    </span>
                </div>
            </div>
            <div v-if="tr.status && tr.status=='created'" class="absolute w-full bottom-0 left-0 right-0" style="height:2px">
                <div v-if="tr.progress && tr.progress>0" class="absolute bg-primary bottom-0 left-0 right-0" style="height:2px" :style="'width:'+tr.progress+'%'"></div>
            </div>
        </div>

        <div v-if="(!transfers && total==null) || loading" class="w-full">
            <Skeleton v-for="i in count" :key="i" height="8rem" class="mb-3 border-round-xl"/>
        </div>

        <div v-if="!loading && total==0" class="w-full">
            <span class="block text-800 mb-3">{{ lang('zero_transfers') }}</span>
        </div>
        
    </div>
    <div v-if="!loading && showNav && transfers && total>0" class="surface-0 border-round-xl overflow-hidden mb-3">
        <Paginator v-model:first="skip" :rows="10" :totalRecords="total" template="PrevPageLink PageLinks NextPageLink " />
    </div>
</template>
  
<script>
import Connect from '@/assets/js/Connect.js'
import Helper from '@/assets/js/Helper.js'
import { BankList, PaymentSystemList } from '@/assets/js/classes/PaymentSystem.js'

export default {
    props: {
        count: {
            type: Number,
            default: 10
        },
        showNav: {
            type: Boolean,
            default: true
        },
    },
    data() {
        return {
            header: {
                en: {title: "My transfers"} ,
                ru: {title: "Мои переводы"} ,
            }, 
            lazyParams: {},
            skip: 0,
            total: null,
            transfers: null,
            banks: [],
            payment_systems: [],
            loading: false
        }
    },
    methods: {
        showMess(severity, summary, detail) {
            this.$toast.add({severity: severity, summary: summary, detail: detail, life: 3000});
        },
        countdown(key, date){
            let countDownDate = new Date(date).getTime();
            setInterval(() => {
                let now = new Date().getTime();
                let distance = countDownDate - now;
                let hours   = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                let minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                let seconds = Math.floor((distance % (1000 * 60)) / 1000);
                let current = Math.floor(distance / 1000 );
                let percent = Math.floor(current / 900 * 100);
                if(seconds.toString().length==1) seconds = '0'+seconds
                this.transfers[key].progress = percent
                this.transfers[key].counter = (hours) ? hours + " : " + minutes + " : " + seconds : minutes + " : " + seconds;
                if (distance <= 0) {
                    this.transfers[key].progress = 0
                    this.transfers[key].counter = null
                }
            }, 1000);
        },
        lang(value){
            return this.$t(`views.personal.transferList.${value}`)
        },
        detail_page(id) {
            this.$router.push({ path: '/personal/transfer/' + id })
        },
        getBankTitle(code){
            let bankObj = new BankList()
            bankObj.items = this.banks
            const bankCode = bankObj.GetTitle(code, this.lang_)
            return (bankCode) ? bankCode : code
        },
        getBankImage(code){
            let bankObj = new BankList()
            bankObj.items = this.banks
            const bankCode = bankObj.GetImage(code)
            return (bankCode) ? bankCode : null
        },
        getPaySysTitle(code){
            let paySysObj = new PaymentSystemList()
            paySysObj.items = this.payment_systems
            const paySysCode = paySysObj.GetTitle(code, this.lang_)
            return (paySysCode) ? paySysCode : code
        },
        getPaySysImage(code){
            let paySysObj = new PaymentSystemList()
            paySysObj.items = this.payment_systems
            const paySysCode = paySysObj.GetImage(code)
            return (paySysCode) ? paySysCode : null
        },
        formatDate(value) {
            return this.helper.format_date_short(value)
        },
        getStatusLabel (status) {
            return this.helper.get_severity_status(status, this.lang_)
        },
        go_to_create(){
            this.$router.push({ path: '/personal/transfer/create/' })
        },
        get_format_amount(value, currency) {
            let digits = 2
            if(currency=='rub' || currency=='idr') digits = 0
            return this.helper.format_amount(value, currency, digits)
        },
        toUpperCase(value) {
            return this.helper.toUpperCase(value)
        },
        get_short_id(value){
            return this.helper.get_short_id(value)
        },
        copy(text){
            this.helper.copy_clipboard(text)
            this.$toast.add({ severity: 'info', summary: this.lang('copied'), detail: text, life: 1000 })
        },
        async LoadTransfers() {
            this.loading = true
            const transfers = await this.connect.user_transfers(this.lazyParams.rows, this.lazyParams.first).then(res => res)
            if (transfers.result && transfers.data) {
                this.transfers = transfers.data
                this.total = transfers.total

                // счетчик для новых переводов
                for (let i = 0; i < this.transfers.length; i++) {
                    if(this.transfers[i].status=='created'){
                        this.countdown(i, this.transfers[i].dates.expired)
                    } else {
                        this.transfers[i].progress = 0
                        this.transfers[i].counter = null
                    }
                }
                this.loading = false
            } else {
                this.transfers = null
                this.total = 0
                this.loading = false

                // выводим ошибки
                if(transfers.message) this.showMess('error', 'Ошибка', transfers.message)
                else this.showMess('error', 'Ошибка', 'Ошибка при получении списка переводов')
            }
        },
        async LoadBanks(){
            this.banks = await BankList.GetList(this.$store).then(r=>r)
            if(!this.banks || !this.banks.length) this.banks = []
        },
        async LoadPaymentSystems(){
            this.payment_systems = await PaymentSystemList.GetList(this.$store).then(r=>r)
            if(!this.payment_systems || !this.payment_systems.length) this.payment_systems = []
        }
    },
    computed: {
        lang_() {
            return (this.$i18n.locale) ? this.$i18n.locale : 'ru'
        },
        banks_obj() {
            return this.$store.state.payment.banks
        }
    },
    watch: {
        skip(){
            window.scrollTo({ top: 0, behavior: 'smooth' })
            this.lazyParams.first = this.skip
            this.LoadTransfers()
        }
    },
    async beforeCreate() {
        this.connect = new Connect()
        this.helper = new Helper()
    },
    mounted() {
        this.lazyParams = {
            first: 0,
            rows: this.count,
            sortField: null,
            sortOrder: null,
            filters: this.filters,
        }
        try { this.LoadTransfers() } catch (error) { console.error('Error loading lazy data:', error) }
        this.LoadBanks()
        this.LoadPaymentSystems()
    },
}
</script>